import { useEffect, useState } from "react";
import axios from "axios";
import "./DropshipzonePage.css";

const API = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
/* HustlePlaza · Dropshipzone catalog sync admin panel.
   Shows live status, lets admin trigger a manual sync, surfaces auth
   errors clearly so the user knows when DSZ needs a Profile-side fix. */

function fmtDt(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-AU", { timeZone: "Australia/Brisbane" });
}

export default function DropshipzonePage() {
  const [status, setStatus] = useState(null);
  const [runs, setRuns] = useState([]);
  const [authErr, setAuthErr] = useState(false);
  const [busy, setBusy] = useState(false);
  const [authTest, setAuthTest] = useState(null);
  const [msg, setMsg] = useState(null);
  const [i18nStatus, setI18nStatus] = useState(null);
  const [i18nBusy, setI18nBusy] = useState(false);

  const refresh = async () => {
    try {
      const [s, r, i] = await Promise.all([
        axios.get(`${API}/api/dropshipzone/status`, { withCredentials: true }),
        axios.get(`${API}/api/dropshipzone/runs?limit=15`, { withCredentials: true }),
        axios.get(`${API}/api/dropshipzone/i18n/status`, { withCredentials: true }),
      ]);
      setStatus(s.data);
      setRuns(r.data || []);
      setI18nStatus(i.data);
    } catch (e) {
      if (e?.response?.status === 401) setAuthErr(true);
    }
  };

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 8000);
    return () => clearInterval(id);
  }, []);

  const testAuth = async () => {
    setAuthTest({ status: "running" });
    try {
      const { data } = await axios.post(
        `${API}/api/dropshipzone/test-auth`,
        {}, { withCredentials: true },
      );
      setAuthTest({ status: "ok", ...data });
    } catch (e) {
      setAuthTest({ status: "fail", detail: e?.response?.data?.detail || "unknown" });
    }
  };

  const triggerSync = async (mode) => {
    if (busy) return;
    setBusy(true);
    setMsg(null);
    try {
      const { data } = await axios.post(
        `${API}/api/dropshipzone/sync/run`,
        { mode, max_products: mode === "full" ? 50000 : 500 },
        { withCredentials: true },
      );
      setMsg({ kind: "ok", text: `Sync started (${mode}). Will run in the background — refresh stats below to watch progress.` });
      setTimeout(refresh, 1500);
    } catch (e) {
      setMsg({ kind: "err", text: e?.response?.data?.detail || "Couldn't start sync." });
    } finally {
      setBusy(false);
    }
  };

  if (authErr) {
    return (
      <div className="dsz-page">
        <h1 className="he-h1 he-h1--ink">Dropshipzone</h1>
        <p>Sign in as an admin to manage the Dropshipzone sync.</p>
      </div>
    );
  }

  if (!status) {
    return <div className="dsz-page"><div className="he-state">Loading…</div></div>;
  }

  return (
    <div className="dsz-page">
      <header className="dsz-head">
        <div>
          <span className="he-eyebrow he-eyebrow--ink">HustlePlaza · Catalog source</span>
          <h1 className="he-h1 he-h1--ink">Dropshipzone</h1>
          <p className="he-admin__sub">
            Full-catalog mirror with images on R2. Self-throttled to {status.rate_limit_per_hour}/hr (DSZ cap is ~600/hr).
          </p>
        </div>
        <div className={`sc-badge sc-badge--${status.cron_running ? "ok" : "off"}`} data-testid="dsz-cron-badge">
          {status.cron_running ? "Cron running" : "Cron stopped"}
        </div>
      </header>

      <section className="dsz-stats">
        <div className="dsz-stat" data-testid="dsz-stat-products">
          <span>Products in DB</span>
          <strong>{status.products_in_db.toLocaleString()}</strong>
        </div>
        <div className="dsz-stat" data-testid="dsz-stat-cats">
          <span>Categories</span>
          <strong>{status.categories_in_db}</strong>
        </div>
        <div className="dsz-stat" data-testid="dsz-stat-images">
          <span>Images on R2</span>
          <strong>{status.images_in_r2.toLocaleString()}</strong>
        </div>
        <div className="dsz-stat">
          <span>R2 storage</span>
          <strong>{status.r2_enabled ? "✓ ready" : "✗ off"}</strong>
        </div>
        <div className="dsz-stat">
          <span>Credentials</span>
          <strong>{status.credentials_configured ? "✓ in .env" : "✗ missing"}</strong>
        </div>
        <div className="dsz-stat">
          <span>Last full sync</span>
          <strong style={{ fontSize: 14 }}>{fmtDt(status.sync_state.last_full_sync_at)}</strong>
        </div>
      </section>

      <section className="dsz-panel">
        <h2 className="he-admin__h2">Live auth check</h2>
        <p className="he-admin__hint">
          Pings <code>POST /auth</code> on Dropshipzone with your credentials. If it fails, DSZ usually wants you to
          enable API access via your Account Manager and grab an API-specific password from your Profile → Integrations.
        </p>
        <button className="he-card__cta" onClick={testAuth} data-testid="dsz-test-auth-btn">
          Test Dropshipzone auth now
        </button>
        {authTest && authTest.status === "ok" && (
          <div className="he-admin__msg he-admin__msg--ok" data-testid="dsz-auth-ok">
            ✓ Auth OK · token <code>{authTest.token_first_8}</code> · expires {fmtDt(authTest.expires_at)}
          </div>
        )}
        {authTest && authTest.status === "fail" && (
          <div className="he-admin__msg he-admin__msg--err" data-testid="dsz-auth-fail">{authTest.detail}</div>
        )}
        {authTest?.status === "running" && (
          <div className="he-admin__msg he-admin__msg--ok">Calling Dropshipzone…</div>
        )}
      </section>

      <section className="dsz-panel">
        <h2 className="he-admin__h2">Trigger a sync</h2>
        <div className="dsz-buttons">
          <button className="he-card__cta" disabled={busy} onClick={() => triggerSync("categories")} data-testid="dsz-sync-cats-btn">
            Sync categories only
          </button>
          <button className="he-card__cta" disabled={busy} onClick={() => triggerSync("incremental")} data-testid="dsz-sync-incremental-btn">
            Incremental (500 products)
          </button>
          <button className="he-card__cta" disabled={busy} onClick={() => triggerSync("full")} data-testid="dsz-sync-full-btn"
                  style={{ background: "var(--he-rust)" }}>
            Full catalog (all products)
          </button>
        </div>
        {msg && <div className={`he-admin__msg he-admin__msg--${msg.kind}`}>{msg.text}</div>}
        <p className="he-admin__hint" style={{ marginTop: 12 }}>
          The hourly cron runs an incremental sync at :15 past every hour. The full-catalog button kicks off an
          immediate large run — only use when you want to backfill from scratch.
        </p>
      </section>

      <section className="dsz-panel">
        <h2 className="he-admin__h2">Translations · auto-multilingual</h2>
        <p className="he-admin__hint">
          Every product&apos;s title + description is auto-translated into 10 languages
          (Italian, Spanish, French, Greek, Arabic, Hindi, Punjabi, Vietnamese,
          Simplified &amp; Traditional Chinese) as it arrives. New products translate
          themselves &mdash; you don&apos;t have to lift a finger. Cached by content hash
          so re-syncs cost nothing.
        </p>
        {i18nStatus && (
          <>
            <div className="dsz-stats" style={{ marginBottom: 12 }}>
              <div className="dsz-stat" data-testid="i18n-total">
                <span>Products</span>
                <strong>{i18nStatus.total_products.toLocaleString()}</strong>
              </div>
              <div className="dsz-stat" data-testid="i18n-complete">
                <span>Fully translated</span>
                <strong>{i18nStatus.fully_translated.toLocaleString()}</strong>
              </div>
              <div className="dsz-stat" data-testid="i18n-pending">
                <span>Pending</span>
                <strong>{i18nStatus.pending.toLocaleString()}</strong>
              </div>
              <div className="dsz-stat" data-testid="i18n-cache">
                <span>Cache entries</span>
                <strong>{i18nStatus.cached_translations.toLocaleString()}</strong>
              </div>
              <div className="dsz-stat">
                <span>Languages</span>
                <strong style={{ fontSize: 16 }}>{i18nStatus.supported_languages.length + 1}</strong>
              </div>
              <div className="dsz-stat">
                <span>LLM key</span>
                <strong>{i18nStatus.llm_configured ? "✓ ready" : "✗ off"}</strong>
              </div>
            </div>
            <button
              type="button"
              className="he-card__cta"
              disabled={i18nBusy || i18nStatus.pending === 0}
              onClick={async () => {
                setI18nBusy(true);
                try {
                  await axios.post(`${API}/api/dropshipzone/i18n/backfill?limit=500`, {}, { withCredentials: true });
                  setMsg({ kind: "ok", text: "Translation backfill started in background (up to 500 products this run)." });
                  setTimeout(refresh, 1500);
                } catch (e) {
                  setMsg({ kind: "err", text: e?.response?.data?.detail || "Couldn't start backfill." });
                } finally {
                  setI18nBusy(false);
                }
              }}
              data-testid="i18n-backfill-btn"
            >
              {i18nBusy ? "Starting…" : "Backfill missing translations (500 / run)"}
            </button>
          </>
        )}
      </section>

      <section className="dsz-panel">
        <h2 className="he-admin__h2">Recent sync runs</h2>
        {runs.length === 0 && <div className="he-state">No runs yet.</div>}
        <table className="dsz-table" data-testid="dsz-runs-table">
          <thead><tr>
            <th>Started</th><th>Mode</th><th>Status</th><th>Products</th><th>Images</th><th>Skipped</th>
          </tr></thead>
          <tbody>
            {runs.map((r) => (
              <tr key={r.id} data-testid={`dsz-run-${r.id}`}>
                <td>{fmtDt(r.started_at)}</td>
                <td>{r.mode}</td>
                <td>
                  <span className={`he-chip he-chip--${r.status === "completed" ? "deal" : (r.status === "failed" ? "soldout" : "deal")}`}>
                    {r.status}
                  </span>
                </td>
                <td>{r.products_synced}</td>
                <td>{r.images_uploaded}</td>
                <td>{r.products_skipped}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}
